/**
 * Copy written from page data.
 *
 * Rules (vision doc §6.5, handoff §A4):
 * - Every sentence comes from a computed fact in the payload. No filler.
 * - Copy variants are chosen by a stable hash of the slug, so pages differ
 *   from one another but never churn between builds.
 * - Agent copy describes measurements only, never intent.
 * - Every fact key in the schema is either handled here or listed as
 *   intentionally ignored (tested).
 */
import {
  describePeriod,
  formatDay,
  formatGap,
  formatInt,
  formatPct,
  formatPoints,
  gapDirection,
  isNum,
  ordinal,
  pickVariant,
} from './format';

const one = (n) => (n === 1 ? '' : 's');

function gapPhrase(value) {
  const direction = gapDirection(value);
  if (direction === 'above') return `${formatPct(value)} above`;
  if (direction === 'below') return `${formatPct(value)} below`;
  return 'within half a percent of';
}

/* ------------------------------------------------------------------ */
/* Suburb                                                              */
/* ------------------------------------------------------------------ */

export function suburbHeadline(payload) {
  const { name, slug, headline, data_period: period } = payload;
  const when = describePeriod(period);
  const n = headline?.guide_matched_sales ?? 0;
  if (!isNum(headline?.guide_gap_pct)) {
    return {
      text: `There aren't enough ${name} sales with a published price guide yet to measure how results compare with guides.`,
    };
  }
  const gap = gapPhrase(headline.guide_gap_pct);
  const variants = [
    `Over ${when}, the typical ${name} home sold ${gap} its advertised price guide.`,
    `In ${when}, ${name} homes typically sold ${gap} the price guide they were marketed with.`,
    `Across ${formatInt(n)} ${name} sales with a published guide in ${when}, the typical result landed ${gap} guide.`,
  ];
  return { text: pickVariant(`${slug}:headline`, variants) };
}

const SUBURB_FACTS = {
  gap_above_city_p75: (fact, payload) => ({
    text: `That puts ${payload.name} in the top quarter of Sydney suburbs for results above guide. A quarter of suburbs sit above ${formatGap(fact.reference)}.`,
    anchor: 'gap',
  }),
  gap_below_city_p25: (fact, payload) => ({
    text: `Results in ${payload.name} land closer to or under guide than in three quarters of Sydney suburbs. The lowest quarter sits below ${formatGap(fact.reference)}.`,
    anchor: 'gap',
  }),
  gap_rank_top_decile: (fact) => ({
    text: `It ranks ${ordinal(fact.value)} of ${formatInt(fact.reference)} suburbs for how far results run above guide.`,
  }),
  gap_rank_bottom_decile: (fact) => ({
    text: `It ranks ${ordinal(fact.value)} of ${formatInt(fact.reference)} suburbs for results above guide, near the bottom of the list.`,
  }),
  yoy_gap_change: (fact) => {
    if (Math.abs(fact.value) < 0.5) return null;
    return {
      text: `The gap has ${fact.value > 0 ? 'widened' : 'narrowed'} by ${formatPoints(Math.abs(fact.value)).replace('+', '')} compared with the previous 12 months.`,
    };
  },
  house_unit_gap_divergence: (fact, payload) => {
    const house = payload.by_type?.house?.guide_gap_pct;
    const unit = payload.by_type?.unit?.guide_gap_pct;
    if (!isNum(house) || !isNum(unit)) return null;
    const wider = house > unit ? 'Houses' : 'Units';
    return {
      text: `${wider} run well ahead of guide compared with ${wider === 'Houses' ? 'units' : 'houses'}: ${formatGap(house)} for houses against ${formatGap(unit)} for units.`,
    };
  },
  auction_share_high: (fact) => ({
    text: `Auctions dominate. ${formatPct(fact.value, 0)} of guide-matched sales went under the hammer${isNum(fact.reference) ? `, against ${formatPct(fact.reference, 0)} across Sydney` : ''}.`,
  }),
  dom_fast_vs_city: (fact) => ({
    text: `Homes sell quickly: a median of ${formatInt(fact.value)} days on market${isNum(fact.reference) ? ` versus ${formatInt(fact.reference)} across Sydney` : ''}.`,
  }),
  dom_slow_vs_city: (fact) => ({
    text: `Homes take longer to sell: a median of ${formatInt(fact.value)} days on market${isNum(fact.reference) ? ` versus ${formatInt(fact.reference)} across Sydney` : ''}.`,
  }),
  price_rank_top_decile: (fact) => ({
    text: `By median sale price it is among Sydney's most expensive suburbs, ranked ${ordinal(fact.value)} of ${formatInt(fact.reference)}.`,
  }),
  agent_concentration_high: (fact, payload) => {
    const top = [...(payload.agents || [])].sort((a, b) => b.guide_matched_sales - a.guide_matched_sales)[0];
    return {
      text: top
        ? `One agent handles a large share of the market: ${top.name} accounts for ${formatPct(fact.value, 0)} of guide-matched sales.`
        : `A single agent accounts for ${formatPct(fact.value, 0)} of guide-matched sales.`,
    };
  },
};

export const SUBURB_FACT_KEYS_HANDLED = Object.keys(SUBURB_FACTS);
export const SUBURB_FACT_KEYS_IGNORED = [];

export function suburbFacts(payload) {
  return (payload.facts || [])
    .map((fact) => SUBURB_FACTS[fact.key]?.(fact, payload) || null)
    .filter(Boolean);
}

export function suburbThinNote(payload) {
  if (payload.indexable) return null;
  const n = payload.headline?.guide_matched_sales ?? 0;
  return `Only ${formatInt(n)} sale${one(n)} here had a published guide in ${describePeriod(payload.data_period)}. Treat these figures as indicative, not settled.`;
}

/* ------------------------------------------------------------------ */
/* Agent                                                               */
/* ------------------------------------------------------------------ */

export function agentHeadline(payload) {
  const { name, slug, headline, data_period: period } = payload;
  const n = headline?.guide_matched_sales ?? 0;
  const when = describePeriod(period);
  if (!isNum(headline?.guide_gap_pct)) {
    return { text: `There aren't enough sales with a published guide to measure ${name}'s results against guide.` };
  }
  const gap = gapPhrase(headline.guide_gap_pct);
  const miss = isNum(headline.guide_error_abs_pct)
    ? ` The typical distance between guide and result, in either direction, was ${formatPct(headline.guide_error_abs_pct)}.`
    : '';
  const variants = [
    `Across ${formatInt(n)} sale${one(n)} with a published guide in ${when}, ${name}'s results landed on average ${gap} the advertised guide.${miss}`,
    `In ${when}, ${name} sold ${formatInt(n)} propert${n === 1 ? 'y' : 'ies'} with a published guide, finishing on average ${gap} guide.${miss}`,
  ];
  return { text: pickVariant(`${slug}:headline`, variants) };
}

const AGENT_FACTS = {
  error_rank_top_decile: (fact) => ({
    text: `Results track guides more closely than most agents: ranked ${ordinal(fact.value)} of ${formatInt(fact.reference)} for smallest typical gap.`,
    anchor: 'error',
  }),
  error_rank_bottom_decile: (fact) => ({
    text: `Results differ from guides by more than most agents: ranked ${ordinal(fact.value)} of ${formatInt(fact.reference)} for smallest typical gap.`,
    anchor: 'error',
  }),
  gap_below_local_suburbs: (fact) => ({
    text: `Results finished closer to guide than is typical where they sell: ${formatGap(fact.value)} against ${formatGap(fact.reference)} for those suburbs overall.`,
  }),
  gap_above_local_suburbs: (fact) => ({
    text: `Results finished further above guide than is typical where they sell: ${formatGap(fact.value)} against ${formatGap(fact.reference)} for those suburbs overall.`,
  }),
  dom_fast_vs_peers: (fact) => ({
    text: `Median time on market was ${formatInt(fact.value)} days, quicker than the ${formatInt(fact.reference)}-day median for agents in the same suburbs.`,
  }),
  dom_slow_vs_peers: (fact) => ({
    text: `Median time on market was ${formatInt(fact.value)} days, slower than the ${formatInt(fact.reference)}-day median for agents in the same suburbs.`,
  }),
  single_suburb_specialist: (fact, payload) => {
    const main = payload.suburbs?.[0];
    return {
      text: main
        ? `A ${main.name} specialist: ${formatPct(fact.value, 0)} of guide-matched sales were there.`
        : `${formatPct(fact.value, 0)} of guide-matched sales were in a single suburb.`,
    };
  },
};

export const AGENT_FACT_KEYS_HANDLED = Object.keys(AGENT_FACTS);
export const AGENT_FACT_KEYS_IGNORED = [];

export function agentFacts(payload) {
  return (payload.facts || [])
    .map((fact) => AGENT_FACTS[fact.key]?.(fact, payload) || null)
    .filter(Boolean);
}

export function agentThinNote(payload) {
  const n = payload.headline?.guide_matched_sales ?? 0;
  if (!payload.index_reasons?.includes('agent_sales_below_threshold')) return null;
  return `Only ${formatInt(n)} sale${one(n)} with a published guide in ${describePeriod(payload.data_period)}. That is too few to rank against other agents, so treat these figures as indicative.`;
}

export function freshnessText(payload) {
  const to = payload?.data_period?.to;
  return to ? `Data to ${formatDay(to)}` : null;
}
